/**
 * Phase 54.7.A — Reusable state-persistence primitive for the ERP.
 *
 * Any list/table/module that owns *filters, search text, pagination,
 * sorting, tabs, branch/employee selection or date filters* MUST use
 * this hook instead of hand-rolling `useState` + `localStorage`.
 *
 * Design goals
 * ------------
 * 1. **Drop-in replacement for `useState`**: same signature (`[value, setValue]`)
 *    plus a third `reset()` helper. Existing code migrates in ~1 line.
 * 2. **Namespaced storage** under `usm.state.<key>` so different modules
 *    (or scoped variants like `inventory.silver` / `inventory.gold`)
 *    never collide.
 * 3. **Forward-compatible merges** — when the initialState object grows
 *    a new field in a later release, previously-persisted state does not
 *    orphan the new field. We spread `{ ...initial, ...persisted }`.
 * 4. **Survives F5 and browser close** via `localStorage`. Session-only
 *    persistence available via `{ storage: "session" }`.
 * 5. **Safe against QuotaExceeded / private-mode / disabled storage** —
 *    every touch is wrapped in try/catch so the app never crashes.
 * 6. **Cross-tab sync** — `storage` event listener keeps two tabs of the
 *    same URL in agreement about the current filter state.
 *
 * Example
 * -------
 *   const [filters, setFilters, resetFilters] = usePersistedState(
 *     "sales",
 *     { q: "", date_from: "", date_to: "", branchId: "", empId: "",
 *       page: 1, pageSize: 50, sortBy: "created_at", sortDir: "desc" },
 *   );
 *
 *   // Reset (e.g. behind a toolbar button)
 *   <button onClick={resetFilters}>Sıfırla</button>
 */
import { useCallback, useEffect, useRef, useState } from "react";

const NAMESPACE = "usm.state.";

function getStore(kind) {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null; // SSR / disabled storage
  }
}

function read(store, fullKey, fallback) {
  if (!store) return fallback;
  try {
    const raw = store.getItem(fullKey);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    // Merge so newly-added keys in `fallback` keep their defaults.
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)
        && fallback && typeof fallback === "object" && !Array.isArray(fallback)) {
      return { ...fallback, ...parsed };
    }
    return parsed;
  } catch {
    return fallback;
  }
}

function write(store, fullKey, value) {
  if (!store) return;
  try { store.setItem(fullKey, JSON.stringify(value)); } catch { /* quota */ }
}

export function usePersistedState(key, initialValue, options = {}) {
  const fullKey = NAMESPACE + key;
  const storeKind = options.storage === "session" ? "session" : "local";
  const store = getStore(storeKind);

  // Snapshot the initial value once — using state so it never changes
  // between renders even if the caller passes an inline literal.
  const initialRef = useRef(initialValue);

  const [state, setState] = useState(() => read(store, fullKey, initialRef.current));

  // Persist on every change.
  useEffect(() => {
    write(store, fullKey, state);
  }, [store, fullKey, state]);

  // Cross-tab sync — when another tab updates the same key, mirror it.
  useEffect(() => {
    if (storeKind !== "local") return;
    const onStorage = (e) => {
      if (e.key !== fullKey) return;
      if (e.newValue == null) {
        setState(initialRef.current);
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue);
        setState(parsed);
      } catch { /* corrupt entry — ignore */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fullKey, storeKind]);

  const reset = useCallback(() => {
    if (store) {
      try { store.removeItem(fullKey); } catch { /* ignore */ }
    }
    setState(initialRef.current);
  }, [store, fullKey]);

  return [state, setState, reset];
}

/**
 * Convenience wrapper for the common "list page filters" pattern.
 *
 * `updater` accepts either a partial object OR a functional updater,
 * so callers can write:
 *
 *   updateFilters({ q: "abc" })          // merge
 *   updateFilters(f => ({ ...f, page: f.page + 1 }))
 */
export function usePersistedFilters(key, initialFilters, options) {
  const [filters, setFilters, reset] = usePersistedState(key, initialFilters, options);
  const update = useCallback((partial) => {
    if (typeof partial === "function") {
      setFilters((prev) => partial(prev));
    } else {
      setFilters((prev) => ({ ...prev, ...partial }));
    }
  }, [setFilters]);
  return { filters, setFilters, updateFilters: update, resetFilters: reset };
}

export default usePersistedState;
